import { BlockStack, Card, Text } from "@shopify/polaris"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import type {
  ThemeBottomSectionImageItem,
  ThemeBottomSectionState,
  ThemeImageBannerRef,
} from "@/models/ITrackingPageOptions"

import { useShowcaseThemeLayout } from "./ShowcaseThemeLayoutContext"
import ShowcaseTopColumnsEditor from "./ShowcaseTopColumnsEditor"
import { mergeThemeBottomSection } from "./themeBottomSectionDefaults"
import ThemeBottomSectionEditor from "./ThemeBottomSectionEditor"
import {
  clampShowcaseWidthToColumnCount,
  getShowcaseTopColumnCount,
  reconcileTopColumnOrder,
} from "./themeLayoutShowcaseDraft"

export type { ThemeLayoutShowcaseContentDraft } from "./themeLayoutShowcaseDraft"

const ThemeLayoutLeftContentShowcaseForm = () => {
  const { t } = useTranslation("tracking_page")
  const { draft, setDraft, setBottomSectionPopupDraft, setTopSectionImagesPopupDraft } = useShowcaseThemeLayout()

  const commitTopSectionImages = useCallback(
    async (next: ThemeBottomSectionImageItem[]) => {
      setDraft((prev) => {
        const columnCount = getShowcaseTopColumnCount(next)
        const nextWidth = clampShowcaseWidthToColumnCount(columnCount, prev.width)

        return {
          ...prev,
          top_section_images: next,
          top_section_column_order: reconcileTopColumnOrder(prev.top_section_column_order, next),
          width: nextWidth,
        }
      })
    },
    [setDraft],
  )

  const commitTopColumnOrder = useCallback(
    async (next: string[]) => {
      setDraft((prev) => ({
        ...prev,
        top_section_column_order: reconcileTopColumnOrder(next, prev.top_section_images),
      }))
    },
    [setDraft],
  )

  const commitBottomSection = useCallback(
    async (next: ThemeBottomSectionState) => {
      setDraft((prev) => ({
        ...prev,
        bottom_section: mergeThemeBottomSection(next),
      }))
    },
    [setDraft],
  )

  const handlePersistBottomItemImage = useCallback(
    async (itemId: string, next: ThemeImageBannerRef | undefined) => {
      setDraft((prev) => {
        const prevSection = prev.bottom_section
        const images = prevSection.images.map((i) =>
          (i.id === itemId ? { ...i, image: next } : i),
        )

        return {
          ...prev,
          bottom_section: mergeThemeBottomSection({
            ...prevSection,
            images,
          }),
        }
      })
    },
    [setDraft],
  )

  return (
    <BlockStack gap="400">
      <Card>
        <Text variant="headingMd" as="h3">
          {t("TopSection")}
        </Text>
        <div className="mt-4">
          <ShowcaseTopColumnsEditor
            columnOrder={draft.top_section_column_order}
            images={draft.top_section_images}
            onCommitImages={commitTopSectionImages}
            onCommitColumnOrder={commitTopColumnOrder}
            onPopupDraftChange={setTopSectionImagesPopupDraft}
          />
        </div>
      </Card>

      <Card padding="0">
        <div className="p-4">
          <Text variant="headingMd" as="h3">
            {t("BottomSection")}
          </Text>
        </div>
        <ThemeBottomSectionEditor
          value={draft.bottom_section}
          onCommit={commitBottomSection}
          onPersistItemImage={handlePersistBottomItemImage}
          onPopupDraftChange={setBottomSectionPopupDraft}
        />
      </Card>
    </BlockStack>
  )
}

export default ThemeLayoutLeftContentShowcaseForm
